import React from 'react';
import { Home, MapPin, Thermometer } from 'lucide-react';

/**
 * Компонент секции основных параметров здания
 */
const BuildingParametersSection = ({
  setFormData, 
  formData, 
  handleInputChange, 
  errors, 
  climateData 
}) => {
  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Home className="w-5 h-5 mr-2 text-red-500" />
        Основные параметры здания
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Тип здания */}
        <div>
          <label htmlFor="buildingType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип здания
          </label>
          <select
            id="buildingType"
            name="buildingType"
            value={formData.buildingType}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="residential">Частный дом</option>
            <option value="apartment">Квартира</option>
            <option value="commercial">Коммерческое здание</option>
          </select>
        </div>

        {/* Регион */}
        <div>
          <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <MapPin className="w-4 h-4 mr-1 text-red-500" />
            Регион
          </label>
          <select
            id="region"
            name="region"
            value={formData.region}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {Object.keys(climateData).map(regionId => (
              <option key={regionId} value={regionId}>
                {climateData[regionId].name} ({climateData[regionId].designTemp}°C)
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Расчетная температура наружного воздуха: {formData.outdoorTemperature}°C
          </p>
        </div>

        {/* Ручной ввод города */}
        <div className="col-span-full">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="useCustomCity"
              name="useCustomCity"
              checked={!climateData[formData.region]?.name}
              onChange={() => {
                if (climateData[formData.region]?.name) {
                  setFormData(prev => ({
                    ...prev,
                    region: 'custom',
                    cityName: climateData[formData.region]?.name || ''
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    region: 'moscow',
                    cityName: '',
                    outdoorTemperature: climateData['moscow'].designTemp
                  }));
                }
              }}
              className="form-checkbox"
            />
            <label htmlFor="useCustomCity" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Указать другой город или населенный пункт
            </label>
          </div>
        </div>

        {formData.region === 'custom' && (
          <>
            <div>
              <label htmlFor="cityName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Название города
              </label>
              <input
                type="text"
                id="cityName"
                name="cityName"
                value={formData.cityName}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.cityName ? 'input-error' : ''}`}
                placeholder="Введите название города"
              />
              {errors.cityName && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.cityName}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="outdoorTemperature" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Thermometer className="w-4 h-4 mr-1 text-red-500" />
                Расчетная температура
              </label>
              <input
                type="number"
                id="outdoorTemperature"
                name="outdoorTemperature"
                value={formData.outdoorTemperature}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.outdoorTemperature ? 'input-error' : ''}`}
                placeholder="Например: -25"
              />
              {errors.outdoorTemperature && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.outdoorTemperature}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Минимальная среднесуточная температура воздуха для вашего региона
              </p>
            </div>
          </>
        )}

        {/* Температура внутри помещения */}
        <div>
          <label htmlFor="indoorTemperature" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Температура внутри помещения, °C
          </label>
          <input
            type="number"
            id="indoorTemperature"
            name="indoorTemperature"
            value={formData.indoorTemperature}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.indoorTemperature ? 'input-error' : ''}`}
            placeholder="Например: 20"
          />
          {errors.indoorTemperature && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.indoorTemperature}
            </p>
          )}
        </div>

        {/* Возраст здания */}
        <div>
          <label htmlFor="buildingAge" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Возраст здания
          </label>
          <select
            id="buildingAge"
            name="buildingAge"
            value={formData.buildingAge}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="new">Новое (после 2000 г.)</option>
            <option value="medium">Среднее (1970-2000 г.)</option>
            <option value="old">Старое (до 1970 г.)</option>
          </select>
        </div>

        {/* Площадь помещения */}
        <div>
          <label htmlFor="floorArea" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Площадь помещения, м²
          </label>
          <input
            type="text"
            id="floorArea"
            name="floorArea"
            value={formData.floorArea}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.floorArea ? 'input-error' : ''}`}
            placeholder="Например: 100"
          />
          {errors.floorArea && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.floorArea}
            </p>
          )}
        </div>

        {/* Высота потолков */}
        <div>
          <label htmlFor="ceilingHeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Высота потолков, м
          </label>
          <input
            type="text"
            id="ceilingHeight"
            name="ceilingHeight"
            value={formData.ceilingHeight}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.ceilingHeight ? 'input-error' : ''}`}
            placeholder="Например: 2.7"
          />
          {errors.ceilingHeight && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.ceilingHeight}
            </p>
          )}
        </div>

        {/* Количество этажей */}
        <div>
          <label htmlFor="floors" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Количество этажей
          </label>
          <input
            type="number"
            id="floors"
            name="floors"
            value={formData.floors}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.floors ? 'input-error' : ''}`}
            placeholder="Например: 1"
            min="1"
            max="50"
            disabled={formData.buildingType === 'apartment'}
          />
          {errors.floors && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.floors}
            </p>
          )}
          {formData.buildingType === 'apartment' && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Для квартиры используется значение 1 этаж
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildingParametersSection;